import { notEmpty } from '@ps/web-core-utils';
import { Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Redirect from '@swf/components/Redirect/Redirect';
import { Vary } from '@swf/components/ResponseHeaders/ResponseHeaders';
import { Header } from '@swf/core/enums';
import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { RootState } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { WidgetFC } from '@swf/core/types/Widget';
import { CompetitionCard, CompetitionTabs, Message, TryAgainMessage } from '@swf/sports/components';
import { Breadcrumb } from '@swf/sports/components/Breadcrumb/Breadcrumb';
import { Tab } from '@swf/sports/components/CompetitionTabs/CompetitionTabs';
import GroupedTemplatePanels from '@swf/sports/components/GroupedTemplatePanels/GroupedTemplatePanels';
import LiveDataWarning from '@swf/sports/components/LiveDataWarning/LiveDataWarning';
import useLiveDataPolling from '@swf/sports/hooks/useLiveDataPolling/useLiveDataPolling';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import { getEventTypesInPlayEventIdsFromMarketViewsMap } from '@swf/sports/utils/getEventTypesInPlayEventIdsMap';

import { TXTWCL_This_competition_is_currently__ELL } from './SportsCompetitionEventsWidget.messages.json';
import { TennisGroups } from './components/TennisGroups';
import { sportsCompetitionEventsWidgetModel, useSportsCompetitionEventsWidget } from './model';

const SportsCompetitionEventsWidget: WidgetFC = () => {
  const {
    widgetSelect,
    widgetDispatch,
    widgetConfig: { shouldApplyTennisGrouping }
  } = useSportsCompetitionEventsWidget();

  const errorCode = useSelector(widgetSelect.errorCode);
  const templateGroups = useSelector(widgetSelect.templateGroups);
  const showOutrights = useSelector(widgetSelect.showOutrights);
  const isLoading = useSelector(widgetSelect.isLoading);
  const markets = useSelector(widgetSelect.markets);
  const marketIds = useSelector(widgetSelect.marketIds);
  const marketTemplateData = useSelector(widgetSelect.marketTemplateData);
  const hasInPlayMarkets = useSelector(widgetSelect.hasInPlayMarkets);
  const eventTypes = useSelector(widgetSelect.eventTypes);
  const urlParams = useSelector(select.sports.getUrlParams);
  const competition = useSelector((state: RootState) =>
    widgetSelect.competition(state, Number(urlParams.competitionId))
  );

  const eventTypesEventIdsMap = useMemo(
    () => getEventTypesInPlayEventIdsFromMarketViewsMap(Object.values(markets).filter(notEmpty)),
    [markets]
  );

  useLiveDataPolling({ eventTypesEventIdsMap });
  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading) {
    return null;
  }

  if (errorCode) {
    if (errorCode === 404) {
      return <Message message={<Translation {...TXTWCL_This_competition_is_currently__ELL} />} />;
    }
    return <TryAgainMessage />;
  }

  if (showOutrights && !templateGroups.length) {
    return <Redirect alias="sportsCompetitionOutrights" values={urlParams} />;
  }

  const { competitionName, logoUrl } = competition;

  return (
    <>
      <Breadcrumb fallbackBreadcrumbText={eventTypes[competition.eventTypeId]?.name} />
      <Vary by={[Header.TimeZone]} />
      <CompetitionCard competitionName={competitionName} logoUrl={logoUrl} />
      {templateGroups.length ? (
        <CompetitionTabs hiddenTabs={showOutrights ? [] : [Tab.OUTRIGHTS]} />
      ) : null}
      {templateGroups.length ? (
        <>
          {shouldApplyTennisGrouping ? (
            <TennisGroups />
          ) : (
            <GroupedTemplatePanels
              groupedOn="DATE"
              groups={templateGroups}
              {...marketTemplateData}
            />
          )}
          {hasInPlayMarkets ? <LiveDataWarning /> : null}
        </>
      ) : null}
    </>
  );
};

SportsCompetitionEventsWidget.model = sportsCompetitionEventsWidgetModel;

export default withWidgetTestId(SportsCompetitionEventsWidget, 'sports-competition-events');
